import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import AdsProvider from '@buzzfeed/adlib/dist/module/bindings/react/components/AdsContext';
import DestinationContext from '../../contexts/destination';
import { getPageContext } from '../../utils/ads/context';
import BuzzContext from '../../contexts/buzz';
import useLegacyTracking from '../../hooks/analytics/useLegacyTracking';
import useABeagleAsync from '../../hooks/useABeagleAsync';

async function getPageTargeting({ buzz, badges, abeagle }) {
  const pageTargeting = {
    bfmono: 'bpage_new',
    bid: buzz.id,
    user: buzz.username,
    nbs:
      !buzz.flags.brand_safe || buzz.flags.nsfw || buzz.flags.sensitive
        ? '1'
        : '0',
    nsfw: buzz.flags.nsfw || !buzz.isModerated ? '1' : '0',
    sensitive: buzz.flags.sensitive ? '1' : '0',
    trending: buzz.tags.includes('--trending') ? '1' : '0',
    tag: buzz.tags
      .map((tag) => tag.match(/^--ad-(\w+)$/)) // https://regex101.com/r/LkRQqb/1
      .filter((match) => match)
      .map((match) => match[1]),
    badges: badges.map((badge) => badge.badge_type),
    brain_tags: [], // @todo
    pagetype: buzz.isCommentsPage ? 'c' : 'b',
  };

  const experiments = [
    'ads_delay_fetch_and_destroy',
    'ads_retail_media_network_exposure',
    'RT-1378-us-quiz-top-sticky-desktop',
    'ads-quiz-top-player-mobile',
    'RT-1559-AdShield-script-on-BFDC',
    'ads_shopping_infinite_scroll',
    'ads_permutive_non_shopping',
    'ads_quiz_experience',
    'RT-2143-lazy-loading-ads',
    'RT-2202-ad-injection-card-style-quiz',
    'RT-2324-lazy-loading-shopping',
    'word-chain-interstitial',
    'huedoku-interstitial',
    'soup-of-the-day-interstitial',
    'geotemp-interstitial',
  ];

  const experimentVariants = await Promise.all(
    experiments.map((experiment) =>
      abeagle.getExperimentVariant(experiment, {
        rejectErrors: false,
        defaultVariantIfUnbucketed: null,
      })
    )
  );

  pageTargeting.abtest = experiments.map(
    (experiment, idx) => `${experiment}|${experimentVariants[idx]}`
  );

  return pageTargeting;
}

export default function Ads(props) {
  const { buzz } = useContext(BuzzContext);

  const badges = props.badges;
  const legacyTracking = useLegacyTracking(buzz);
  const abeagle = useABeagleAsync();
  const { base_url } = useContext(DestinationContext);

  const getPageContextBound = useCallback(() => {

    return getPageContext({
      base_url,
      buzz,
      abeagle,
      tracking: legacyTracking,
    });
  }, [buzz.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPageTargetingBound = useCallback(
    () => getPageTargeting({ buzz, badges, abeagle }),
    [buzz.id] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // const getSlotTargetingBound = useCallback(({ wid, adPos }) => {}, []);

  return (
    <AdsProvider
      pageId={buzz.id}
      pageLanguage={buzz.language}
      adsEnabled={!buzz.disableAds}
      getPageContext={getPageContextBound}
      getPageTargeting={getPageTargetingBound}
      // getSlotTargeting={getSlotTargetingBound}
    >
      {props.children}
    </AdsProvider>
  );
}

Ads.propTypes = {
  buzz: PropTypes.object.isRequired,
  badges: PropTypes.array.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};
